import type { CSSProperties, ReactNode } from 'react';

import { PAGE_MAX } from '@/features/storefront/components/containerWidth';
import { cn } from '@/lib/cn';

/**
 * How wide one section's column runs, relative to the page column.
 *
 * Reading matter is not read at 72rem: a contact form or a paragraph of prose
 * stretched the full width of a wide theme is a line the eye loses its place
 * in. Those sections ask for `narrow` or `tight` and get the *lesser* of what
 * they asked for and what the theme allows, so a compact layout family stays
 * compact rather than being widened by a section that wanted less.
 *
 * A prop rather than a `max-w-*` class because the width below is an inline
 * style, which beats any class: a section passing one was silently rendering
 * at the full width of the page, which is exactly how the contact form ended
 * up spanning the window.
 */
export type StorefrontContainerWidth = 'page' | 'narrow' | 'tight';

const WIDTHS: Record<StorefrontContainerWidth, string> = {
  page: PAGE_MAX,
  narrow: `min(${PAGE_MAX}, 48rem)`,
  tight: `min(${PAGE_MAX}, 36rem)`,
};

/**
 * The storefront's page column, wide or narrow as the layout family says.
 *
 * The shared `ui/Container` hardcodes the platform's width and keeps it - the
 * dashboard stands outside the theme on purpose. Everything the theme *does*
 * own goes through this one - the shop's pages, its cart and its checkout -
 * whose width is the
 * `--storefront-container-max` variable set by `themeVariables()`, with the
 * classic width as the fallback for anything rendered outside a themed shell.
 */
export function StorefrontContainer({
  className,
  width = 'page',
  style,
  children,
}: {
  className?: string;
  width?: StorefrontContainerWidth;
  /** Painted over the column's own width - colours and spacing, never a width. */
  style?: CSSProperties;
  children: ReactNode;
}) {
  // A section the merchant gave a width of its own says so through
  // `--section-content-max`, set on the section by `SectionView`; unset, the
  // section's own width stands. See `contentWidthFor`.
  return (
    <div
      className={cn('mx-auto w-full px-5 sm:px-8', className)}
      style={{ ...style, maxWidth: `var(--section-content-max, ${WIDTHS[width]})` }}
    >
      {children}
    </div>
  );
}
